import { getBrandIcon } from "@/assets/brands";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  isDesktop: boolean;
  data?: Array<{
    rank: number;
    brandName: string;
    avgPosition: number;
    visibilityScore: number;
    change: string;
  }>;
}

// 创建矢量图标组件
const BrandIcon: React.FC<{ brandName: string; size: number }> = ({
  brandName,
  size,
}) => {
  const [imageError, setImageError] = React.useState(false);
  const iconPath = getBrandIcon(brandName);

  // 获取默认配置
  const getDefaultConfig = (name: string) => {
    switch (name) {
      case "理想":
        return { bg: "#21C4B8", text: "理想" };
      case "蔚来":
        return { bg: "#21C4B8", text: "蔚来" };
      case "比亚迪":
        return { bg: "#21C4B8", text: "BYD" };
      case "小米":
        return { bg: "#21C4B8", text: "MI" };
      default:
        return { bg: "#6B7280", text: "?" };
    }
  };

  const config = getDefaultConfig(brandName);

  // 如果图标路径存在且没有加载错误，显示图片
  if (iconPath && !imageError) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: 6,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={iconPath}
          alt={brandName}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
          onError={() => setImageError(true)}
        />
      </div>
    );
  }

  // 显示文字备用方案
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 6,
        background: config.bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        fontSize: size * 0.3,
        fontWeight: "bold",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {config.text}
    </div>
  );
};

/**
 * 首页「竞争态势分析」装饰图封装
 * 提取自 SearchIndex，对外仅暴露尺寸自适应。
 */
export const IndexBrandComparisonChart: React.FC<Props> = ({ isDesktop }) => {
  const { t } = useTranslation();
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<any>();

  const data = [
    {
      rank: 1,
      brandName: t("common.charts.brand") + " A",
      avgPosition: 1.8,
      visibilityScore: "78%",
      change: "",
    },
    {
      rank: 2,
      brandName: t("common.charts.brand") + " B",
      avgPosition: 2.5,
      visibilityScore: "67%",
      change: "",
    },
    {
      rank: 3,
      brandName: t("common.charts.brand") + " C",
      avgPosition: 3.1,
      visibilityScore: "51%",
      change: "",
    },
    {
      rank: 4,
      brandName: t("common.charts.brand") + " D",
      avgPosition: 3.8,
      visibilityScore: "46%",
      change: "",
    },
    {
      rank: 5,
      brandName: t("common.charts.brand") + " E",
      avgPosition: 4.5,
      visibilityScore: "35.5%",
      change: "",
    },
    {
      rank: 6,
      brandName: t("common.charts.brand") + " F",
      avgPosition: 5.1,
      visibilityScore: "27.8%",
      change: "",
    },
  ];

  // 自动焦点切换逻辑
  useEffect(() => {
    const autoFocus = () => {
      if (!isUserInteracting) {
        setFocusedIndex((prev) => (prev + 1) % data.length);
      }
    };

    // 延迟2秒开始自动演示
    const startTimer = setTimeout(() => {
      intervalRef.current = setInterval(autoFocus, 3000); // 每2秒切换一次
    }, 3000);

    return () => {
      clearTimeout(startTimer);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isUserInteracting, data.length]);

  // 自动滚动到焦点行
  useEffect(() => {
    if (containerRef.current && focusedIndex >= 1) {
      // 从第三行开始需要滚动
      const container = containerRef.current;
      const rowHeight = isDesktop ? 50 : 40; // 估算每行高度
      const scrollTop = (focusedIndex - 1) * rowHeight; // 滚动位置，让焦点行居中显示

      container.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });
    } else if (containerRef.current && focusedIndex < 2) {
      // 前两行时滚动回顶部
      containerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [focusedIndex, isDesktop]);

  // 监听用户交互
  const handleUserInteraction = () => {
    setIsUserInteracting(true);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // 3秒后恢复自动演示
    setTimeout(() => {
      setIsUserInteracting(false);
      intervalRef.current = setInterval(() => {
        setFocusedIndex((prev) => (prev + 1) % data.length);
      }, 5000);
    }, 6000);
  };

  return (
    <div
      style={{
        height: isDesktop ? "300px" : "240px",
        background: "#ffffff",
        position: "relative",
      }}
    >
      {/* 品牌排名表格 */}
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: 6,
          width: "100%",
          height: "100%",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          display: "flex",
          flexDirection: "column",
          padding: "10px",
        }}
      >
        {/* 表头 */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "60px 1fr 120px",
            padding: isDesktop ? "12px 16px" : "8px 12px",
            backgroundColor: "#f8f9fa",
            color: "#666",
            fontWeight: 600,
            borderBottom: "1px solid #e0e0e0",
            alignItems: "center",
            fontSize: isDesktop ? 12 : 10,
          }}
        >
          <div style={{ textAlign: "center" }}>
            {t("home_components.chart_rank")}
          </div>
          <div>{t("home_components.chart_brand")}</div>
          <div style={{ textAlign: "center" }}>
            {t("api.metric_mention_rate")}
          </div>
        </div>

        {/* 表格主体 */}
        <div
          ref={containerRef}
          style={{
            flex: 1,
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            position: "relative",
          }}
          onMouseEnter={handleUserInteraction}
          onClick={handleUserInteraction}
        >
          {data.map((item, index) => (
            <div
              key={item.brandName}
              style={{
                display: "grid",
                gridTemplateColumns: "60px 1fr 120px",
                padding: isDesktop ? "10px 0" : "8px 0",
                borderBottom:
                  index === data.length - 1 ? "none" : "1px solid #f0f0f0",
                alignItems: "center",
                color: "#333",
                fontSize: isDesktop ? 13 : 11,
                backgroundColor:
                  index === focusedIndex ? "#e0f2fe" : "transparent",
                transition: "all 0.3s ease",
                transform: index === focusedIndex ? "scale(1.1)" : "scale(1)",
                boxShadow:
                  index === focusedIndex
                    ? "0 2px 8px rgba(14, 165, 233, 0.15)"
                    : "none",
                margin: index === focusedIndex ? "2px 0" : "0",
                zIndex: index === focusedIndex ? 1 : 0,
                position: "relative",
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  color: "#666",
                  textAlign: "center",
                }}
              >
                {item.rank}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: isDesktop ? 8 : 6,
                  fontWeight: 500,
                }}
              >
                <span>{item.brandName}</span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span style={{ fontWeight: 600 }}>{item.visibilityScore}</span>
                <span
                  style={{
                    color: item.change.startsWith("+") ? "#10b981" : "#ef4444",
                    fontWeight: 500,
                    fontSize: isDesktop ? 12 : 10,
                  }}
                >
                  {item.change}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IndexBrandComparisonChart;
